<template>
  <div>
    <div class="wd-flex wd-flex-col wd-gap-2 wd-w-full">
      <!-- Card Number Field -->
      <div
        id="card-number"
        class="wd-h-10 wd-w-full wd-border-1 wd-border-solid wd-rounded wd-px-4 wd-border-gray50 hover:wd-border-black50"
        :class="{ 'wd-border-red50 hover:wd-border-red50': cardStatus.hasCardNumberError }"
      ></div>

      <!-- Card Number Error Messages -->
      <div v-show="hasCardNumberError" class="wd-text-red50 wd-text-sm wd--mt-1">
        <span v-if="hasFundingError">{{ $t('credit_card_error_debit_card_not_accept') }}</span>
        <span v-else>{{ cardNumberErrorMsg }}</span>
      </div>

      <!-- Expiration Date and CVC Fields -->
      <div class="wd-flex wd-gap-2 wd-w-full">
        <div>
          <div id="card-expiration-date" class="tpfield" :class="{ '!wd-border-red50': cardStatus.hasCardExpiryDatesError }"></div>
          <div v-show="cardStatus.hasCardExpiryDatesError" class="wd-text-red50 wd-text-sm wd-mt-1">
            {{ hasBindingCardExpiryDateError ? $t('credit_card_error_vaild_date_not_enough') : $t('credit_card_error') }}
          </div>
        </div>
        <div class="wd-inline-flex">
          <div id="card-ccv" class="tpfield" :class="{ '!wd-border-red50': cardStatus.hasCCVError }"></div>
          <div class="wd--ml-8 wd-mt-2.5 wd-cursor-pointer" @click.stop="openCreditCardQuestion">
            <funnow-icon max-width="20" :image-path="'question-icon__gray50.svg'" />
          </div>
        </div>
      </div>
    </div>

    <credit-card-question-modal ref="creditCardQuestionModal" />
  </div>
</template>

<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import FunnowIcon from '@/components/icons/funnow-icon.vue'
import CreditCardQuestionModal from '@/components/dialog/payment/credit-card-question.vue'
import { TapPayFieldStatus, TapPaySDK, type TPCardUpdateCallback, type TPGetPrimeParameter } from '@/utils/payment/payment'
import { useSetupTapPay } from '@/composition/useSetupTapPay'
import { useOrderPaymentStore } from '@/store-pinia/order-payment'
import { PaymentMethodProviderType, type TPCardFunding, type TPCardResult } from '@/view-models/payment'

type PropsType = {
  isInstallment?: boolean
  supportBanks?: string[]
  supportFundings?: TPCardFunding[]
  isBindingCard?: boolean
}

const i18n = useI18n()
const route = useRoute()
const { $store } = useNuxtApp()

useSetupTapPay(initTapPay)

const props = withDefaults(defineProps<PropsType>(), {
  isInstallment: false,
  supportBanks: () => [],
  supportFundings: () => [],
  isBindingCard: false,
})

const emit = defineEmits<{
  'update:cardVerified': [value: boolean]
}>()

// Store setup
const orderStore = useOrderPaymentStore()
const { orderDetail } = storeToRefs(orderStore)
const orderId = computed(() => orderDetail.value?.id)

const creditCardQuestionModal = useTemplateRef('creditCardQuestionModal')

const isCardReady = defineModel<boolean>('cardReady', { default: false })
const cardStatus = reactive({
  canGetPrime: false,
  hasCardFundingError: false,
  hasCardNumberError: false,
  hasCardExpiryDatesError: false,
  hasCCVError: false,
  hasSupportBankError: false,
})

const hasBindingCardExpiryDateError = computed(() => {
  return props.isBindingCard && cardStatus.hasCardExpiryDatesError
})

const { checkIsInvalidCard } = usePaymentCardInfo()

// Computed
const binCode = computed(() => $store.getters['payment/binCode'])

const hasFundingError = computed(() => {
  return cardStatus.hasCardFundingError
})

const hasCardNumberError = computed(() => !!(hasFundingError.value || cardNumberErrorMsg.value))

const cardNumberErrorMsg = computed(() => {
  if (cardStatus.hasCardFundingError) {
    return i18n.t('credit_card_error_debit_card_not_accept')
  }
  if (cardStatus.hasCardNumberError) {
    return i18n.t('credit_card_error')
  }
  return ''
})

// Lifecycle
onMounted(() => {
  if (binCode.value === null) {
    fetchBinCode()
  }
})

// Methods
function fetchBinCode() {
  $store.dispatch('payment/getBinCodeList', { t: route.query?.t, orderId: orderId.value }).catch((err: any) => {
    console.error('fetchBinCode errors:', err)
  })
}

function checkBinCode(cardNumber: string) {
  let code = 0
  if (!binCode.value) {
    return code
  }
  binCode.value.forEach((data: any) => {
    if (cardNumber.indexOf(data.code) === 0) {
      code = data.id
    }
  })
  return code
}

async function createTapPayToken(): Promise<TPCardResult> {
  const result = await TapPaySDK.getPrime()

  if (result.card !== undefined && result.card.prime && result.status === 0 && result.msg.toLowerCase() === 'success') {
    return result as unknown as TPCardResult
  }

  throw result
}

async function verifyCardForm(cardPayload: Parameters<TPCardUpdateCallback>[0]) {
  cardStatus.canGetPrime = cardPayload.canGetPrime

  cardStatus.hasCardNumberError = cardPayload.status.number === TapPayFieldStatus.ERROR
  cardStatus.hasCardExpiryDatesError = cardPayload.status.expiry === TapPayFieldStatus.ERROR
  cardStatus.hasCCVError = cardPayload.status.ccv === TapPayFieldStatus.ERROR

  if (!cardPayload.canGetPrime || cardPayload.hasError) {
    syncFormIsValid(false)
    return
  }

  // 如果是綁定卡片模式，需要進行額外的驗證
  if (props.isBindingCard) {
    try {
      const cardResult = await createTapPayToken()

      // 檢查卡片類型（排除 debit card）
      cardStatus.hasCardFundingError = checkIsInvalidCard(PaymentMethodProviderType.TapPay, cardResult)

      if (cardStatus.hasCardFundingError) {
        syncFormIsValid(false)
        return
      }
    } catch (error) {
      console.error('Card verification error:', error)
      syncFormIsValid(false)
      return
    }
  }

  syncFormIsValid(true)
}

const cardUpdateCallback = useDebounceFn<TPCardUpdateCallback>(async cardPayload => {
  await verifyCardForm(cardPayload)
}, 500)

function syncFormIsValid(isValid: boolean) {
  isCardReady.value = isValid
  emit('update:cardVerified', isValid)
}

function initTapPay() {
  isCardReady.value = false

  TapPaySDK.init({
    number: {
      // css selector
      element: '#card-number',
      // placeholder: '0000 0000 0000 0000',
      placeholder: i18n.t('credit_card_hint'),
    },
    expirationDate: {
      // DOM object
      element: '#card-expiration-date',
      placeholder: 'MM / YY',
    },
    ccv: {
      element: '#card-ccv',
      placeholder: 'CVC / CVV',
    },
  })

  TapPaySDK.onCardUpdate(cardUpdateCallback)
  syncFormIsValid(false)
}

function openCreditCardQuestion() {
  creditCardQuestionModal.value?.showModal()
}

// 獲取卡片的 prime token
async function getPrime() {
  try {
    const primePayload = await createTapPayToken()
    return {
      ...primePayload,
      bin_code_id: checkBinCode(primePayload?.card.bincode || ''),
    }
  } catch (error) {
    console.error('Get prime error:', error)
    return null
  }
}

// Expose methods for parent component access
defineExpose({
  getPrime,
})
</script>

<style>
.tpfield {
  @apply wd-h-10 wd-w-full wd-border-1 wd-border-solid wd-rounded wd-px-4 wd-border-gray50 hover:wd-border-black50;
}
.tappay-field-focus {
  @apply wd-border-2 wd-border-orange50 hover:wd-border-orange50;
}
</style>
